/**
 * Authentication Context
 *
 * Manages Google OAuth authentication state and provides
 * sign-in/sign-out functionality to the application.
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  type ReactNode,
} from 'react';
import { googleDrive } from '@/services/googleDrive';
import { openRouter } from '@/services/openRouter';
import { supabase } from '@/lib/supabase';
import type { SupabaseClient } from '@supabase/supabase-js';
import type { User } from '@/types';

// ============================================
// TYPES
// ============================================

interface AuthContextValue {
  user: User | null;
  isLoading: boolean;
  isInitialized: boolean;
  error: string | null;
  signIn: () => Promise<void>;
  signOut: () => void;
  clearError: () => void;
  /** Supabase client for feed pages. Null if Supabase is not configured. */
  supabaseClient: SupabaseClient | null;
  /** Whether the Supabase session is active (feed features available) */
  isSupabaseReady: boolean;
}

// ============================================
// CONTEXT
// ============================================

const AuthContext = createContext<AuthContextValue | null>(null);

// ============================================
// PROVIDER
// ============================================

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSupabaseReady, setIsSupabaseReady] = useState(false);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * Establish a Supabase session for feed features via Edge Function token exchange.
   * Sends the Google access token to our Edge Function, which validates it with Google,
   * finds/creates the matching Supabase user, and returns a magic link token.
   * Best-effort — if it fails, personal items still work; only feed features are affected.
   */
  const initSupabaseSession = useCallback(async () => {
    if (!supabase) return;

    try {
      // Check if we already have an active Supabase session
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (session) {
        setIsSupabaseReady(true);
        return;
      }

      // Exchange Google access token for Supabase session via Edge Function
      const googleToken = googleDrive.getAccessToken();
      if (!googleToken) {
        console.warn('No Google access token — feed features unavailable');
        return;
      }

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;
      if (!supabaseUrl || !supabaseAnonKey) return;

      const res = await fetch(`${supabaseUrl}/functions/v1/exchange-google-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${supabaseAnonKey}`,
        },
        body: JSON.stringify({ google_access_token: googleToken }),
      });

      if (!res.ok) {
        const errBody = await res.text();
        console.warn('Token exchange failed:', res.status, errBody);
        return;
      }

      const { hashed_token } = (await res.json()) as { hashed_token: string };

      // Verify the magic link token → creates a real Supabase session
      const { error: verifyError } = await supabase.auth.verifyOtp({
        token_hash: hashed_token,
        type: 'magiclink',
      });

      if (verifyError) {
        console.warn('Supabase verify OTP failed:', verifyError.message);
        return;
      }

      setIsSupabaseReady(true);
    } catch (err) {
      console.warn('Supabase session init failed:', err);
    }
  }, []);

  // Schedule a silent token refresh ~5 min before expiry
  const scheduleTokenRefresh = useCallback(() => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);

    const expiry = Number(localStorage.getItem('google_token_expiry') ?? '0');
    const delay = expiry - Date.now() - 5 * 60 * 1000; // 5 min before expiry

    const doRefresh = () => {
      googleDrive
        .refreshToken()
        .then((userData) => {
          setUser(userData);
          scheduleTokenRefresh(); // chain next refresh
        })
        .catch(() => {
          console.warn('Silent token refresh failed — signing out');
          if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
          refreshTimerRef.current = null;
          googleDrive.signOut();
          openRouter.clearApiKey();
          if (supabase) void supabase.auth.signOut();
          setIsSupabaseReady(false);
          setUser(null);
        });
    };

    // Always run from setTimeout so browsers block any unsolicited popup
    refreshTimerRef.current = setTimeout(doRefresh, Math.max(delay, 1000));
  }, []);

  // Initialize Google Drive service on mount
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        await googleDrive.initialize();

        // If we have a stored token, check if it's still valid
        if (googleDrive.isSignedIn()) {
          const tokenExpiry = localStorage.getItem('google_token_expiry');
          const isExpired = tokenExpiry && Date.now() > Number(tokenExpiry);

          if (isExpired) {
            // Token expired — clear it and let user click sign in
            googleDrive.signOut();
          } else {
            // Token still valid — validate it
            try {
              const response = await fetch('https://www.googleapis.com/oauth2/v2/userinfo', {
                headers: { Authorization: `Bearer ${googleDrive.getAccessToken()}` },
              });

              if (response.ok) {
                const userData = (await response.json()) as User;
                setUser(userData);
                scheduleTokenRefresh();
                void initSupabaseSession();
              } else {
                // Token rejected by Google — clear it and let user click sign in
                googleDrive.signOut();
              }
            } catch {
              googleDrive.signOut();
            }
          }
        }
      } catch (err) {
        console.error('Failed to initialize auth:', err);
        setError('Failed to initialize authentication');
      } finally {
        setIsInitialized(true);
      }
    };

    void initializeAuth();

    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sign in handler
  const signIn = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const userData = await googleDrive.signIn();
      setUser(userData);
      scheduleTokenRefresh();
      // Best-effort Supabase session (don't block sign-in on this)
      void initSupabaseSession();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Sign in failed';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [scheduleTokenRefresh, initSupabaseSession]);

  // Sign out handler
  const signOut = useCallback(() => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    refreshTimerRef.current = null;
    googleDrive.signOut();
    openRouter.clearApiKey();
    if (supabase) void supabase.auth.signOut();
    setIsSupabaseReady(false);
    setUser(null);
    setError(null);
  }, []);

  // Clear error handler
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const value: AuthContextValue = {
    user,
    isLoading,
    isInitialized,
    error,
    signIn,
    signOut,
    clearError,
    supabaseClient: supabase,
    isSupabaseReady,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ============================================
// HOOK
// ============================================

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}

// ============================================
// UTILITY HOOKS
// ============================================

/**
 * Hook that returns user if authenticated, otherwise throws
 * Use in components that require authentication
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useRequireAuth(): User {
  const { user } = useAuth();

  if (!user) {
    throw new Error('User must be authenticated');
  }

  return user;
}

/**
 * Hook that returns whether user is authenticated
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useIsAuthenticated(): boolean {
  const { user, isInitialized } = useAuth();
  return isInitialized && !!user;
}
